import { AlertService, AlertRule } from '../../../services/alert.ts';
import { authService } from '../../../services/auth.ts';
import { ToastService } from '../../../services/toast.ts';
import '../../../components/library/ui/app-card.ts';
import '../../../components/library/ui/app-button.ts';
import '../../../components/library/ui/app-icon.ts';
import '../../../components/alerts/alert-editor.ts';
import '../../../components/library/form/form-input.ts';
import '../../../components/library/form/form-select.ts';
import { injectAesthetic } from '../../../components/library/ui/shared-styles.ts';

export class AppAlerts extends HTMLElement {
  private _rules: AlertRule[] = [];
  private _isLoading = true;
  private _searchText: string = '';
  private _filterStatus: string = 'all';
  private _filterMetric: string = 'all';
  private _alertService = AlertService.getInstance();
  private _toastService = ToastService.getInstance();
  private _orgId: string = '';
  private _error: string | null = null;
  private _editorOpen = false;
  private _editingRuleId: string | null = null;

  static get observedAttributes() {
    return ['org-id'];
  }

  constructor() {
    super();
  }

  attributeChangedCallback(name: string, oldVal: string, newVal: string) {
    if (name === 'org-id' && oldVal !== newVal) {
      this._orgId = newVal;
      if (this.isConnected) {
        this.loadAlerts();
      }
    }
  }

  async connectedCallback() {
    this.render();
    
    try {
      const attrOrgId = this.getAttribute('org-id');
      if (attrOrgId) {
        this._orgId = attrOrgId;
      } else {
        const user = authService.getUser();
        this._orgId = user?.org_id || '';
      }
      
      if (!this._orgId) {
        const profile = await authService.getProfile();
        this._orgId = profile?.org_id || '';
      }
      
      if (!this._orgId) {
        this._isLoading = false;
        this.render();
        return;
      }

      await this.loadAlerts();
    } catch (e) {
      console.error('Alerts initialization failed:', e);
      this._isLoading = false;
      this.render();
    }
    
    requestAnimationFrame(() => this.emitPanel());
  }

  private async loadAlerts() {
    if (!this._orgId) return;
    
    this._isLoading = true;
    this._error = null;
    this.render();
    try {
      this._rules = await this._alertService.listByOrganization(this._orgId);
    } catch (e: any) {
      console.error('[AppAlerts] Failed to load alerts:', e);
      this._error = e.message || "An unknown error occurred.";
      // Toast handled by ApiClient
    } finally {
      this._isLoading = false;
      this.render();
    }
  }

  emitPanel() {
    this.dispatchEvent(new CustomEvent('shell-panel-changed', {
      detail: { 
        panel: null,
        header: {
          title: 'Alerts',
          subtitle: 'Monitor and manage system notifications'
        }
      },
      bubbles: true,
      composed: true
    }));
  }

  private openEditor(ruleId: string | null = null) {
    this._editingRuleId = ruleId;
    this._editorOpen = true;
    this.render();
  }

  private closeEditor() {
    this._editorOpen = false;
    this._editingRuleId = null;
    this.render();
  }

  private renderSparklineBars(data: number[] | undefined): string {
    const points = data || [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    const max = Math.max(...points, 0.1);
    const count = points.length;
    const spacing = 2;
    const barWidth = (100 - (count - 1) * spacing) / count;

    return points.map((val, i) => {
      const h = Math.max(4, (val / max) * 36);
      const x = i * (barWidth + spacing);
      const y = 40 - h;
      return `<rect x="${x}" y="${y}" width="${barWidth}" height="${h}" rx="1" />`;
    }).join('');
  }

  private getAlertState(rule: AlertRule): 'firing' | 'warning' | 'healthy' | 'disabled' {
    if (!rule.enabled) return 'disabled';
    if (rule.firing) return 'firing';
    
    const history = rule.history || [];
    const currentValue = history.length > 0 ? history[history.length - 1] : 0;
    const warningThreshold = rule.threshold * 0.8;

    if (currentValue >= warningThreshold) return 'warning';
    return 'healthy';
  }

  private formatMetricType(type: string): string {
    const mapping: Record<string, string> = {
      'error_rate_5xx': '5xx Error Rate',
      'error_count_5xx': '5xx Error Count',
      'error_rate_4xx': '4xx Error Rate',
      'error_count_4xx': '4xx Error Count',
      'error_rate_spike_5xx': '5xx Error Rate Spike',
      'error_rate_spike_4xx': '4xx Error Rate Spike',
      'traffic_drop': 'Traffic Drop',
      'traffic_spike': 'Traffic Spike'
    };
    return mapping[type] || type;
  }

  private renderAlertRow(rule: AlertRule) {
    const state = this.getAlertState(rule);
    const bars = this.renderSparklineBars(rule.history);
    
    return `
      <div class="activity-row ${state}" data-id="${rule.id}">
        <div class="status-indicator">
          <div class="status-dot ${state}"></div>
        </div>
        
        <div class="rule-content">
          <div class="rule-main">
            <div class="rule-header">
              <span class="rule-name">${rule.name}</span>
              <span class="metric-badge">${this.formatMetricType(rule.metric_type)}</span>
              ${!rule.enabled ? '<span class="disabled-badge">DISABLED</span>' : ''}
            </div>
            <div class="rule-details">
              Alert if <span class="accent">${this.formatMetricType(rule.metric_type)}</span> 
              is <span class="accent">above ${rule.threshold}</span> 
              over <span class="accent">${rule.time_window}m</span>
            </div>
          </div>

          <div class="sparkline-wrapper">
            <div class="sparkline-container">
              <svg viewBox="0 0 100 40" preserveAspectRatio="none">
                <g class="sparkline-bars" style="fill: var(--accent); opacity: 0.8; filter: url(#filt-sketch-${'alerts-page'.split('').reduce((a, b) => a + b.charCodeAt(0), 0)})">
                  ${bars}
                </g>
              </svg>
            </div>
            <div class="current-value ${state}">
              ${rule.history && rule.history.length > 0 ? rule.history[rule.history.length - 1].toFixed(1) : '0.0'}
            </div>
          </div>
        </div>

        <div class="action-group">
          <app-button variant="ghost" size="sm" icon="settings" class="edit-rule-btn" data-id="${rule.id}"></app-button>
        </div>
      </div>
    `;
  }

  render() {
    const filteredRules = this._rules.filter(rule => {
      const matchesSearch = !this._searchText || 
        rule.name.toLowerCase().includes(this._searchText.toLowerCase()) ||
        rule.metric_type.toLowerCase().includes(this._searchText.toLowerCase());
      
      const state = this.getAlertState(rule);
      const matchesStatus = this._filterStatus === 'all' || this._filterStatus === state;
      
      const matchesMetric = this._filterMetric === 'all' || rule.metric_type === this._filterMetric;

      return matchesSearch && matchesStatus && matchesMetric;
    });

    this.innerHTML = `
      <style>
        .alerts-page {
          padding: 0;
          position: relative;
        }
        
        .filter-bar {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
          align-items: flex-end;
          flex-wrap: wrap;
          padding: 16px 20px;
          background: rgba(255, 255, 255, 0.01);
          border: 1px solid var(--border-sketch);
          border-radius: 8px;
        }

        .filter-bar form-input, .filter-bar form-select {
          margin-bottom: 0;
          min-width: 180px;
        }

        .activity-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .activity-row {
          display: grid;
          grid-template-columns: 32px 1fr 40px;
          align-items: center;
          gap: 20px;
          padding: 12px 24px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid var(--border-sketch);
          position: relative;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .activity-row::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: linear-gradient(90deg, var(--accent), transparent);
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 0;
        }

        .activity-row.firing::before {
          background: linear-gradient(90deg, rgba(239, 68, 68, 0.1), transparent);
          opacity: 1;
        }

        .activity-row:hover {
          background: rgba(255, 255, 255, 0.04);
          border-color: rgba(245, 158, 11, 0.4);
          transform: translateY(-2px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.3);
        }

        .status-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: currentColor;
          position: relative;
        }

        .status-dot::after {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          border-radius: 50%;
          background: currentColor;
          animation: status-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          opacity: 0.4;
        }

        .status-dot.healthy { color: #10b981; }
        .status-dot.warning { color: #f59e0b; }
        .status-dot.firing { color: #ef4444; }
        .status-dot.disabled { color: #6b7280; opacity: 0.5; }

        .status-dot.disabled::after { display: none; }

        @keyframes status-pulse {
          0% { transform: scale(1); opacity: 0.4; }
          70% { transform: scale(3); opacity: 0; }
          100% { transform: scale(1); opacity: 0; }
        }

        .rule-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 40px;
          position: relative;
          z-index: 2;
        }

        .rule-main { flex: 1; }

        .rule-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 4px;
        }

        .rule-name {
          font-family: var(--font-sketch);
          font-size: 1.2rem;
          color: var(--text-main);
          letter-spacing: 0.01em;
        }

        .metric-badge {
          font-family: var(--font-mono);
          font-size: 0.65rem;
          padding: 4px 10px;
          background: rgba(255,255,255,0.04);
          border: 1px solid var(--border-sketch);
          border-radius: 4px;
          color: var(--text-dim);
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .disabled-badge {
          font-family: var(--font-mono);
          font-size: 0.6rem;
          padding: 2px 8px;
          background: rgba(255,255,255,0.05);
          color: var(--text-muted);
          border-radius: 3px;
        }

        .rule-details {
          font-size: 0.95rem;
          color: var(--text-dim);
          font-family: 'Inter', sans-serif;
          line-height: 1.5;
        }

        .accent { color: var(--accent); font-weight: 600; }

        .sparkline-wrapper {
          display: flex;
          align-items: center;
          gap: 20px;
          width: 240px;
          padding-right: 10px;
        }

        .sparkline-container {
          flex: 1;
          height: 50px;
          position: relative;
        }

        .sparkline-container svg {
          width: 100%;
          height: 100%;
          overflow: visible;
        }

        .sparkline-container rect {
          transition: all 0.3s ease;
        }

        .activity-row:hover .sparkline-container rect {
          opacity: 1;
        }

        .current-value {
          font-family: var(--font-mono);
          font-size: 1.1rem;
          font-weight: 700;
          min-width: 50px;
          text-align: right;
          letter-spacing: -0.05em;
        }

        .current-value.healthy { color: #10b981; }
        .current-value.warning { color: #f59e0b; }
        .current-value.firing { color: #ef4444; }

        .action-group {
          display: flex;
          justify-content: flex-end;
          opacity: 0.3;
          transition: all 0.3s ease;
          position: relative;
          z-index: 5;
        }

        .activity-row:hover .action-group {
          opacity: 1;
        }

        .empty-state {
          padding: 120px 40px;
          text-align: center;
          border: 1px dashed var(--border-sketch);
          background: rgba(255,255,255,0.01);
          border-radius: 8px;
        }

        .loading-state {
          padding: 100px;
          text-align: center;
          color: var(--text-dim);
          font-family: var(--font-mono);
          font-size: 1.2rem;
          letter-spacing: 0.3em;
          animation: pulse-text 2s infinite;
        }

        @keyframes pulse-text {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      </style>

      <div class="alerts-page">
        <div class="filter-bar">
          <form-input placeholder="Search rules..." value="${this._searchText}" id="filter-search" style="flex: 2;"></form-input>
          
          <form-select id="filter-status" value="${this._filterStatus}" label="Status" options='[
            {"value": "all", "label": "All Statuses"},
            {"value": "firing", "label": "Firing"},
            {"value": "warning", "label": "Warning"},
            {"value": "healthy", "label": "Healthy"},
            {"value": "disabled", "label": "Disabled"}
          ]'></form-select>
          
          <form-select id="filter-metric" value="${this._filterMetric}" label="Metric" options='[
            {"value": "all", "label": "All Metrics"},
            {"value": "error_rate_5xx", "label": "5xx Error Rate"},
            {"value": "error_count_5xx", "label": "5xx Error Count"},
            {"value": "error_rate_4xx", "label": "4xx Error Rate"},
            {"value": "error_count_4xx", "label": "4xx Error Count"},
            {"value": "error_rate_spike_5xx", "label": "5xx Error Rate Spike"},
            {"value": "error_rate_spike_4xx", "label": "4xx Error Rate Spike"},
            {"value": "traffic_drop", "label": "Traffic Drop"},
            {"value": "traffic_spike", "label": "Traffic Spike"}
          ]'></form-select>
          
          <div style="flex: 1;"></div>
          
          <app-button variant="white" size="md" icon="plus" id="create-alert-btn" square data-tooltip="New Alert Rule"></app-button>
        </div>

        ${injectAesthetic('alerts-page')}

        ${this._error ? `
          <div class="empty-state">
            <div style="font-size: 3rem; margin-bottom: 24px;">📡</div>
            <h2 style="font-family: var(--font-sketch); margin-bottom: 12px;">Link Interrupted</h2>
            <p style="color: var(--text-muted); margin-bottom: 32px;">${this._error}</p>
            <app-button variant="primary" onclick="window.location.reload()">Reconnect Engine</app-button>
          </div>
        ` : this._isLoading ? `
          <div class="loading-state">SYNCHRONIZING_METRIC_RULES...</div>
        ` : `
          <div class="activity-list">
            ${filteredRules.length > 0 ? filteredRules.map(rule => this.renderAlertRow(rule)).join('') : `
              <div class="empty-state">
                <div style="margin-bottom: 2rem; opacity: 0.6;">
                  <app-icon name="bell-off" size="xl" style="transform: scale(2.5);"></app-icon>
                </div>
                <h2 style="font-family: var(--font-sketch); margin-bottom: 12px;">Quiet in the sector</h2>
                <p style="color: var(--text-muted); margin-bottom: 32px;">You haven't configured any alert rules yet.</p>
              </div>
            `}
          </div>
        `}

        ${this._editorOpen ? `
          <app-alert-editor 
            org-id="${this._orgId}" 
            rule-id="${this._editingRuleId || 'new'}"
            id="active-editor">
          </app-alert-editor>
        ` : ''}
      </div>
    `;

    this.setupEventListeners();
  }

  private setupEventListeners() {
    this.querySelector('#create-alert-btn')?.addEventListener('click', () => this.openEditor());
    
    // Filter events
    this.querySelector('#filter-search')?.addEventListener('form-input', (e: any) => {
      this._searchText = e.detail.value;
      this.render();
      
      // Restore focus to the input after render
      setTimeout(() => {
        const input = this.querySelector('#filter-search') as any;
        if (input) {
          const inner = input.shadowRoot?.querySelector('input');
          if (inner) {
            inner.focus();
            inner.setSelectionRange(this._searchText.length, this._searchText.length);
          }
        }
      }, 0);
    });

    this.querySelector('#filter-status')?.addEventListener('change', (e: any) => {
      this._filterStatus = e.detail.value;
      this.render();
    });

    this.querySelector('#filter-metric')?.addEventListener('change', (e: any) => {
      this._filterMetric = e.detail.value;
      this.render();
    });

    this.querySelectorAll('.edit-rule-btn').forEach(btn => {
      btn.addEventListener('click', (e: any) => {
        const id = btn.getAttribute('data-id');
        this.openEditor(id);
      });
    });

    const editor = this.querySelector('#active-editor');
    if (editor) {
      editor.addEventListener('close', () => this.closeEditor());
      editor.addEventListener('save', () => {
        this.loadAlerts();
        this.closeEditor();
      });
    }
  }
}

if (!customElements.get('app-alerts')) {
  customElements.define('app-alerts', AppAlerts);
}
